import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Encabezado, Pestanas } from '../navegacion/Marco';
import {
  Aviso,
  Boton,
  Cajon,
  Campo,
  Cargando,
  Dato,
  Desplazable,
  ErrorEnPantalla,
  Insignia,
  Panel,
  Selector,
  Vacio,
  clases,
} from '../comun/componentes';
import { useAccion, useDatos } from '../comun/useDatos';
import { enviar } from '../api/cliente';
import { atraso, fecha, gs, hoyIso, inicioDeMes, legible, porcentaje } from '../comun/formato';
import { useSesion } from '../sesion/Sesion';
import { BuscadorDePaciente } from './FichaPaciente';
import type {
  CajaVista,
  CobroRegistrado,
  ComisionProfesional,
  Deuda,
  LiquidacionVista,
  MetodoCobro,
  PacienteResumen,
  PlanCuotas,
  ProductividadProfesional,
  ReporteIngresos,
} from '../api/tipos';

const SECCIONES = [
  { valor: 'caja', rotulo: 'Caja del día' },
  { valor: 'cuentas', rotulo: 'Cuentas por cobrar' },
  { valor: 'liquidaciones', rotulo: 'Convenios' },
  { valor: 'reportes', rotulo: 'Reportes' },
];

/** Finanzas: caja, cobranza, convenios y reportes, en cuatro secciones. */
export function Finanzas() {
  const { seccion = 'caja' } = useParams();
  const navegar = useNavigate();

  return (
    <>
      <Encabezado titulo="Finanzas" detalle="Cobranza del día, deuda, convenios y gestión." />

      <Pestanas
        actual={seccion}
        opciones={SECCIONES}
        alCambiar={(valor) => navegar(`/finanzas/${valor}`)}
      />

      {seccion === 'caja' && <Caja />}
      {seccion === 'cuentas' && <CuentasPorCobrar />}
      {seccion === 'liquidaciones' && <Liquidaciones />}
      {seccion === 'reportes' && <Reportes />}
    </>
  );
}

function Caja() {
  const { puede } = useSesion();
  const caja = useDatos<CajaVista>('/api/v1/caja');
  const cajas = useDatos<{ id: string; nombre: string }[]>('/api/v1/caja/cajas');
  const { correr, ejecutando, error } = useAccion();
  const [cobrando, setCobrando] = useState(false);
  const [cerrando, setCerrando] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);

  const abrir = async () => {
    const hecho = await correr(() =>
      enviar('/api/v1/caja/abrir', { cajaId: cajas.datos?.[0]?.id, montoInicialGs: 0 }),
    );

    if (hecho !== null) {
      caja.recargar();
    }
  };

  if (caja.cargando) return <Cargando que="la caja" />;

  // Sin caja abierta el 404 es el estado normal de la mañana, no un error.
  if (!caja.datos) {
    return (
      <Panel>
        <Vacio
          titulo="La caja del día todavía no está abierta"
          detalle="Abrila para poder registrar cobros. Al cerrarla vas a poder hacer el arqueo."
          accion={
            puede('caja.abrir') && (
              <Boton variante="primario" cargando={ejecutando} onClick={() => void abrir()}>
                Abrir caja
              </Boton>
            )
          }
        />
        {error && <Aviso tono="alerta">{error}</Aviso>}
      </Panel>
    );
  }

  const enCajon = caja.datos.porMetodo.filter((m) => m.afectaCajaFisica);
  const enBanco = caja.datos.porMetodo.filter((m) => !m.afectaCajaFisica);

  return (
    <div className="space-y-4">
      {aviso && (
        <Aviso tono="exito" alCerrar={() => setAviso(null)}>
          {aviso}
        </Aviso>
      )}

      <div className="grid gap-3 tablet:grid-cols-3">
        <Panel>
          <p className="etiqueta-campo">Cobrado hoy</p>
          <p className="cifra mt-1 text-xl font-semibold text-tinta-900 tablet:text-2xl">
            {gs(caja.datos.totalCobrado)}
          </p>
          <p className="mt-1 text-dato text-tinta-500">{fecha(caja.datos.dia)}</p>
        </Panel>
        <Panel>
          <p className="etiqueta-campo">Esperado en el cajón</p>
          <p className="cifra mt-1 text-xl font-semibold text-tinta-900 tablet:text-2xl">
            {gs(caja.datos.esperadoEnCajon)}
          </p>
          <p className="mt-1 text-dato text-tinta-500">
            Incluye {gs(caja.datos.montoInicial)} de apertura
          </p>
        </Panel>
        <Panel>
          <p className="etiqueta-campo">Acciones</p>
          <div className="mt-2 flex flex-col gap-2">
            {puede('cobro.registrar') && (
              <Boton variante="primario" onClick={() => setCobrando(true)}>
                Registrar cobro
              </Boton>
            )}
            {puede('caja.cerrar') && <Boton onClick={() => setCerrando(true)}>Cerrar y arquear</Boton>}
          </div>
        </Panel>
      </div>

      <div className="grid gap-4 tablet:grid-cols-2">
        <Panel titulo="En el cajón" descripcion="Efectivo y todo lo que se cuenta a mano">
          <TablaMetodos metodos={enCajon} />
        </Panel>
        <Panel titulo="En el banco" descripcion="Débito y QR acreditan al instante; crédito, a 48 horas">
          <TablaMetodos metodos={enBanco} />
        </Panel>
      </div>

      <RegistrarCobro
        abierto={cobrando}
        alCerrar={() => setCobrando(false)}
        alCobrar={(cobro) => {
          setCobrando(false);
          setAviso(
            `Cobro ${cobro.numero} registrado por ${gs(cobro.montoGs)}${
              cobro.comprobante ? ` · comprobante ${cobro.comprobante}` : ''
            }.`,
          );
          caja.recargar();
        }}
      />

      <CerrarCaja
        abierto={cerrando}
        caja={caja.datos}
        alCerrar={() => setCerrando(false)}
        alArquear={(diferencia) => {
          setCerrando(false);
          setAviso(
            diferencia === 0
              ? 'Caja cerrada. El arqueo cuadró exacto.'
              : `Caja cerrada con una diferencia de ${gs(diferencia)}.`,
          );
          caja.recargar();
        }}
      />
    </div>
  );
}

function TablaMetodos({ metodos }: { metodos: CajaVista['porMetodo'] }) {
  if (metodos.length === 0) {
    return <Vacio titulo="Sin movimientos" detalle="Todavía no se cobró por estos medios." />;
  }

  return (
    <Desplazable>
      <table className="tabla">
        <thead>
          <tr>
            <th scope="col">Método</th>
            <th scope="col" className="text-right">Cobrado</th>
            <th scope="col" className="text-right">Acreditado</th>
          </tr>
        </thead>
        <tbody>
          {metodos.map((metodo) => (
            <tr key={metodo.metodoCobroId}>
              <td>{metodo.metodo}</td>
              <td className="cifra text-right">{gs(metodo.total)}</td>
              <td
                className={clases(
                  'cifra text-right',
                  metodo.acreditado < metodo.total && 'text-aviso-700',
                )}
              >
                {gs(metodo.acreditado)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Desplazable>
  );
}

function RegistrarCobro({
  abierto,
  alCerrar,
  alCobrar,
}: {
  abierto: boolean;
  alCerrar: () => void;
  alCobrar: (cobro: CobroRegistrado) => void;
}) {
  const { correr, ejecutando, error } = useAccion();
  const metodos = useDatos<MetodoCobro[]>(abierto ? '/api/v1/cobros/metodos' : null);
  const [paciente, setPaciente] = useState<PacienteResumen | null>(null);
  const [busquedaReinicios, setBusquedaReinicios] = useState(0);
  const [formas, setFormas] = useState<{ metodoCobroId: string; montoGs: string; referencia: string }[]>(
    [{ metodoCobroId: '', montoGs: '', referencia: '' }],
  );

  const total = formas.reduce((suma, forma) => suma + (Number(forma.montoGs) || 0), 0);

  const cobrar = async () => {
    const cobro = await correr(() =>
      enviar<CobroRegistrado>('/api/v1/cobros', {
        pacienteId: paciente?.id,
        formas: formas
          .filter((f) => f.metodoCobroId && Number(f.montoGs) > 0)
          .map((f) => ({
            metodoCobroId: f.metodoCobroId,
            montoGs: Number(f.montoGs),
            referencia: f.referencia.trim() || null,
          })),
      }),
    );

    if (cobro) {
      setFormas([{ metodoCobroId: '', montoGs: '', referencia: '' }]);
      setPaciente(null);
      setBusquedaReinicios((n) => n + 1);
      alCobrar(cobro);
    }
  };

  return (
    <Cajon
      abierto={abierto}
      titulo="Registrar cobro"
      alCerrar={alCerrar}
      pie={
        <div className="space-y-2">
          <p className="flex items-baseline justify-between text-sm">
            <span className="text-tinta-500">Total</span>
            <span className="cifra text-lg font-semibold text-tinta-900">{gs(total)}</span>
          </p>
          <div className="flex gap-2">
            <Boton
              variante="primario"
              ancho
              cargando={ejecutando}
              disabled={!paciente || total <= 0}
              onClick={() => void cobrar()}
            >
              Cobrar
            </Boton>
            <Boton onClick={alCerrar}>Cancelar</Boton>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        <BuscadorDePaciente
          key={busquedaReinicios}
          id="cobro-paciente"
          elegidoId={paciente?.id}
          alElegir={setPaciente}
          ocultarListaTrasElegir
        />

        {paciente && <SaldoDelPaciente pacienteId={paciente.id} />}

        <div className="space-y-3">
          <p className="etiqueta-campo">Formas de pago</p>

          {formas.map((forma, indice) => {
            const metodo = metodos.datos?.find((m) => m.id === forma.metodoCobroId);

            return (
              <div key={indice} className="space-y-2 rounded-lg border border-papel-200 p-3">
                <Selector
                  etiqueta="Método"
                  value={forma.metodoCobroId}
                  onChange={(e) => {
                    const copia = [...formas];
                    copia[indice] = { ...forma, metodoCobroId: e.target.value };
                    setFormas(copia);
                  }}
                >
                  <option value="">Elegí el método</option>
                  {(metodos.datos ?? []).map((opcion) => (
                    <option key={opcion.id} value={opcion.id}>
                      {opcion.nombre}
                    </option>
                  ))}
                </Selector>

                <Campo
                  etiqueta="Monto (Gs)"
                  inputMode="numeric"
                  value={forma.montoGs}
                  onChange={(e) => {
                    const copia = [...formas];
                    copia[indice] = { ...forma, montoGs: e.target.value.replace(/\D/g, '') };
                    setFormas(copia);
                  }}
                />

                {metodo?.requiereReferencia && (
                  <Campo
                    etiqueta="Referencia"
                    ayuda="Número de transacción, cheque o últimos dígitos de la tarjeta."
                    value={forma.referencia}
                    onChange={(e) => {
                      const copia = [...formas];
                      copia[indice] = { ...forma, referencia: e.target.value };
                      setFormas(copia);
                    }}
                  />
                )}

                {formas.length > 1 && (
                  <Boton
                    variante="silencioso"
                    onClick={() => setFormas(formas.filter((_, i) => i !== indice))}
                  >
                    Quitar esta forma
                  </Boton>
                )}
              </div>
            );
          })}

          <Boton
            ancho
            onClick={() => setFormas([...formas, { metodoCobroId: '', montoGs: '', referencia: '' }])}
          >
            Agregar otra forma de pago
          </Boton>
        </div>

        {error && (
          <Aviso tono="alerta">{error}</Aviso>
        )}
      </div>
    </Cajon>
  );
}

function SaldoDelPaciente({ pacienteId }: { pacienteId: string }) {
  const { datos } = useDatos<Deuda[]>('/api/v1/cuentas-por-cobrar');
  const deuda = datos?.find((d) => d.pacienteId === pacienteId);

  if (!deuda) {
    return (
      <p className="rounded-lg bg-exito-50 px-3 py-2 text-dato text-exito-700">
        El paciente no tiene saldo pendiente.
      </p>
    );
  }

  return (
    <div className="rounded-lg bg-aviso-50 px-3 py-2 text-dato text-aviso-700">
      Saldo pendiente: <strong className="cifra">{gs(deuda.saldo)}</strong>
      {deuda.cuotasVencidas > 0 && ` · ${deuda.cuotasVencidas} cuotas vencidas (${atraso(deuda.diasDeAtraso)})`}
    </div>
  );
}

function CerrarCaja({
  abierto,
  caja,
  alCerrar,
  alArquear,
}: {
  abierto: boolean;
  caja: CajaVista;
  alCerrar: () => void;
  alArquear: (diferencia: number) => void;
}) {
  const { correr, ejecutando, error } = useAccion();
  const [declarado, setDeclarado] = useState('');

  const diferencia = (Number(declarado) || 0) - caja.esperadoEnCajon;

  const cerrar = async () => {
    const resultado = await correr(() =>
      enviar<{ diferencia: number }>('/api/v1/caja/cerrar', {
        aperturaId: caja.id,
        montoDeclaradoGs: Number(declarado) || 0,
      }),
    );

    if (resultado) {
      setDeclarado('');
      alArquear(resultado.diferencia);
    }
  };

  return (
    <Cajon
      abierto={abierto}
      titulo="Cerrar y arquear"
      alCerrar={alCerrar}
      pie={
        <div className="flex gap-2">
          <Boton variante="primario" ancho cargando={ejecutando} onClick={() => void cerrar()}>
            Cerrar caja
          </Boton>
          <Boton onClick={alCerrar}>Cancelar</Boton>
        </div>
      }
    >
      <div className="space-y-4">
        <dl className="space-y-3">
          <Dato rotulo="Esperado en el cajón">
            <span className="cifra text-lg font-semibold">{gs(caja.esperadoEnCajon)}</span>
          </Dato>
        </dl>

        <Campo
          etiqueta="Contado en el cajón (Gs)"
          inputMode="numeric"
          autoFocus
          value={declarado}
          onChange={(e) => setDeclarado(e.target.value.replace(/\D/g, ''))}
          ayuda="Contá el efectivo y escribí el total, sin ajustar."
        />

        {declarado && (
          <div
            className={clases(
              'rounded-lg px-3 py-2 text-sm',
              diferencia === 0
                ? 'bg-exito-50 text-exito-700'
                : Math.abs(diferencia) < 10000
                  ? 'bg-aviso-50 text-aviso-700'
                  : 'bg-alerta-50 text-alerta-700',
            )}
          >
            {diferencia === 0
              ? 'Cuadra exacto.'
              : diferencia > 0
                ? `Sobran ${gs(diferencia)}.`
                : `Faltan ${gs(Math.abs(diferencia))}.`}
          </div>
        )}

        {error && (
          <Aviso tono="alerta">{error}</Aviso>
        )}
      </div>
    </Cajon>
  );
}

function CuentasPorCobrar() {
  const { datos, cargando, error, recargar } = useDatos<Deuda[]>('/api/v1/cuentas-por-cobrar');
  const [detalle, setDetalle] = useState<Deuda | null>(null);

  if (cargando) return <Cargando que="las cuentas" />;
  if (error) return <ErrorEnPantalla mensaje={error} reintentar={recargar} />;

  const total = (datos ?? []).reduce((suma, deuda) => suma + deuda.saldo, 0);

  return (
    <>
      <Panel
        titulo="Cuentas por cobrar"
        descripcion={`${datos?.length ?? 0} pacientes · ${gs(total)} pendientes`}
      >
        {datos?.length === 0 && (
          <Vacio titulo="Nadie debe nada" detalle="Todos los planes de cuotas están al día." />
        )}

        {datos && datos.length > 0 && (
          <Desplazable>
            <table className="tabla">
              <thead>
                <tr>
                  <th scope="col">Paciente</th>
                  <th scope="col" className="text-right">Saldo</th>
                  <th scope="col" className="text-right">Vencidas</th>
                  <th scope="col">Atraso</th>
                  <th scope="col" />
                </tr>
              </thead>
              <tbody>
                {datos.map((deuda) => (
                  <tr key={deuda.planCuotasId}>
                    <td>
                      <Link
                        to={`/pacientes/${deuda.pacienteId}`}
                        className="font-medium text-tinta-900 hover:text-marca-700"
                      >
                        {deuda.paciente}
                      </Link>
                      {deuda.telefono && (
                        <span className="block text-dato text-tinta-400">{deuda.telefono}</span>
                      )}
                    </td>
                    <td className="cifra text-right font-medium">{gs(deuda.saldo)}</td>
                    <td className="cifra text-right">{deuda.cuotasVencidas}</td>
                    <td>
                      <Insignia
                        tono={
                          deuda.diasDeAtraso > 60 ? 'alerta' : deuda.diasDeAtraso > 0 ? 'aviso' : 'exito'
                        }
                      >
                        {atraso(deuda.diasDeAtraso)}
                      </Insignia>
                    </td>
                    <td className="text-right">
                      <Boton onClick={() => setDetalle(deuda)}>Ver cuotas</Boton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Desplazable>
        )}
      </Panel>

      <CuotasDelPlan deuda={detalle} alCerrar={() => setDetalle(null)} />
    </>
  );
}

function CuotasDelPlan({ deuda, alCerrar }: { deuda: Deuda | null; alCerrar: () => void }) {
  const { datos } = useDatos<PlanCuotas>(deuda ? `/api/v1/cuotas/${deuda.planCuotasId}` : null, [
    deuda?.planCuotasId,
  ]);

  return (
    <Cajon abierto={deuda != null} titulo={deuda?.paciente ?? ''} alCerrar={alCerrar}>
      {!datos && <Cargando que="las cuotas" />}

      {datos && (
        <div className="space-y-4">
          <dl className="grid grid-cols-2 gap-3">
            <Dato rotulo="Financiado">{gs(datos.montoTotal)}</Dato>
            <Dato rotulo="Entrega">{gs(datos.entrega)}</Dato>
            <Dato rotulo="Pagado">{gs(datos.totalPagado)}</Dato>
            <Dato rotulo="Saldo">
              <span className="font-semibold">{gs(datos.saldoPendiente)}</span>
            </Dato>
          </dl>

          <Desplazable>
            <table className="tabla">
              <thead>
                <tr>
                  <th scope="col">N.º</th>
                  <th scope="col">Vence</th>
                  <th scope="col" className="text-right">Monto</th>
                  <th scope="col">Estado</th>
                </tr>
              </thead>
              <tbody>
                {datos.cuotas.map((cuota) => (
                  <tr key={cuota.id}>
                    <td className="cifra">{cuota.numero}</td>
                    <td className="cifra whitespace-nowrap">{fecha(cuota.vencimiento)}</td>
                    <td className="cifra text-right">{gs(cuota.monto)}</td>
                    <td>
                      <Insignia
                        tono={
                          cuota.estado === 'Pagada'
                            ? 'exito'
                            : cuota.estado === 'Vencida'
                              ? 'alerta'
                              : 'neutro'
                        }
                      >
                        {legible(cuota.estado)}
                      </Insignia>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Desplazable>
        </div>
      )}
    </Cajon>
  );
}

function Liquidaciones() {
  const { datos, cargando, error, recargar } = useDatos<LiquidacionVista[]>('/api/v1/liquidaciones');
  const [detalle, setDetalle] = useState<LiquidacionVista | null>(null);

  if (cargando) return <Cargando que="las liquidaciones" />;
  if (error) return <ErrorEnPantalla mensaje={error} reintentar={recargar} />;

  return (
    <>
      <Panel titulo="Liquidaciones a convenios" descripcion="Lo que hay que facturarle a cada empresa">
        {datos?.length === 0 && (
          <Vacio
            titulo="Sin liquidaciones"
            detalle="Se arman por convenio y por mes, con lo atendido bajo ese convenio."
          />
        )}

        {datos && datos.length > 0 && (
          <Desplazable>
            <table className="tabla">
              <thead>
                <tr>
                  <th scope="col">Convenio</th>
                  <th scope="col">Período</th>
                  <th scope="col">Estado</th>
                  <th scope="col" className="text-right">Pacientes</th>
                  <th scope="col" className="text-right">Total</th>
                  <th scope="col" />
                </tr>
              </thead>
              <tbody>
                {datos.map((liquidacion) => (
                  <tr key={liquidacion.id}>
                    <td className="font-medium">{liquidacion.convenio}</td>
                    <td className="cifra whitespace-nowrap">
                      {liquidacion.periodo.slice(0, 7).split('-').reverse().join('/')}
                    </td>
                    <td>
                      <Insignia
                        tono={
                          liquidacion.estado === 'Cobrada'
                            ? 'exito'
                            : liquidacion.estado === 'Borrador'
                              ? 'neutro'
                              : 'marca'
                        }
                      >
                        {legible(liquidacion.estado)}
                      </Insignia>
                    </td>
                    <td className="cifra text-right">{liquidacion.pacientes}</td>
                    <td className="cifra text-right font-medium">{gs(liquidacion.totalGs)}</td>
                    <td className="text-right">
                      <Boton onClick={() => setDetalle(liquidacion)}>Ver planilla</Boton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Desplazable>
        )}
      </Panel>

      <Cajon
        abierto={detalle != null}
        titulo={`${detalle?.convenio ?? ''} · ${detalle?.periodo.slice(0, 7) ?? ''}`}
        alCerrar={() => setDetalle(null)}
      >
        {detalle && (
          <div className="space-y-3">
            <dl className="grid grid-cols-2 gap-3">
              <Dato rotulo="Total atendido">{gs(detalle.totalAtendidoGs)}</Dato>
              <Dato rotulo="Paga el convenio">
                <span className="font-semibold">{gs(detalle.totalGs)}</span>
              </Dato>
            </dl>

            <Desplazable>
              <table className="tabla">
                <thead>
                  <tr>
                    <th scope="col">Fecha</th>
                    <th scope="col">Paciente</th>
                    <th scope="col" className="text-right">Convenio</th>
                  </tr>
                </thead>
                <tbody>
                  {detalle.items.map((item) => (
                    <tr key={item.id}>
                      <td className="cifra whitespace-nowrap">{fecha(item.fecha)}</td>
                      <td>
                        <span className="block">{item.paciente}</span>
                        <span className="block text-dato text-tinta-400">{item.prestacion}</span>
                      </td>
                      <td className="cifra text-right">{gs(item.importeConvenioGs)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Desplazable>
          </div>
        )}
      </Cajon>
    </>
  );
}

function Reportes() {
  const [desde, setDesde] = useState(inicioDeMes(hoyIso()));
  const [hasta, setHasta] = useState(hoyIso());
  const rango = `desde=${desde}&hasta=${hasta}`;

  const ingresos = useDatos<ReporteIngresos>(`/api/v1/reportes/ingresos?${rango}`, [desde, hasta]);
  const productividad = useDatos<ProductividadProfesional[]>(
    `/api/v1/reportes/productividad?${rango}`,
    [desde, hasta],
  );
  const comisiones = useDatos<ComisionProfesional[]>(`/api/v1/reportes/comisiones?${rango}`, [
    desde,
    hasta,
  ]);

  return (
    <div className="space-y-4">
      <Panel titulo="Período">
        <div className="grid gap-3 tablet:grid-cols-2">
          <Campo etiqueta="Desde" type="date" value={desde} onChange={(e) => setDesde(e.target.value)} />
          <Campo etiqueta="Hasta" type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} />
        </div>
      </Panel>

      {ingresos.error && <ErrorEnPantalla mensaje={ingresos.error} reintentar={ingresos.recargar} />}

      {ingresos.datos && (
        <Panel titulo="Ingresos" descripcion="Cobranza del período por método">
          <div className="mb-4 grid gap-3 tablet:grid-cols-4">
            <Dato rotulo="Cobrado">
              <span className="cifra text-xl font-semibold tablet:text-2xl">{gs(ingresos.datos.cobradoGs)}</span>
            </Dato>
            <Dato rotulo="Acreditado">{gs(ingresos.datos.acreditadoGs)}</Dato>
            <Dato rotulo="Producción">{gs(ingresos.datos.produccionGs)}</Dato>
            <Dato rotulo="Por cobrar">{gs(ingresos.datos.porCobrarGs)}</Dato>
          </div>

          <Desplazable>
            <table className="tabla">
              <thead>
                <tr>
                  <th scope="col">Método</th>
                  <th scope="col" className="text-right">Cobrado</th>
                  <th scope="col" className="text-right">Participación</th>
                </tr>
              </thead>
              <tbody>
                {ingresos.datos.porMetodo.map((metodo) => (
                  <tr key={metodo.metodoId}>
                    <td>{metodo.metodo}</td>
                    <td className="cifra text-right">{gs(metodo.cobradoGs)}</td>
                    <td className="text-right">
                      <span className="inline-flex items-center gap-2">
                        <span aria-hidden="true" className="h-1.5 w-16 overflow-hidden rounded-full bg-papel-200">
                          <span
                            className="block h-full rounded-full bg-marca-500"
                            style={{ width: `${metodo.participacion}%` }}
                          />
                        </span>
                        <span className="cifra">{porcentaje(metodo.participacion)}</span>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Desplazable>
        </Panel>
      )}

      {productividad.datos && productividad.datos.length > 0 && (
        <Panel titulo="Productividad por profesional">
          <Desplazable>
            <table className="tabla">
              <thead>
                <tr>
                  <th scope="col">Profesional</th>
                  <th scope="col" className="text-right">Citas</th>
                  <th scope="col" className="text-right">Atendidas</th>
                  <th scope="col" className="text-right">Ausentismo</th>
                  <th scope="col" className="text-right">Producción</th>
                  <th scope="col" className="text-right">Cobrado</th>
                </tr>
              </thead>
              <tbody>
                {productividad.datos.map((fila) => (
                  <tr key={fila.profesionalId}>
                    <td>
                      <span className="block font-medium">{fila.profesional}</span>
                      {fila.especialidad && (
                        <span className="block text-dato text-tinta-400">{fila.especialidad}</span>
                      )}
                    </td>
                    <td className="cifra text-right">{fila.citasAgendadas}</td>
                    <td className="cifra text-right">{fila.citasAtendidas}</td>
                    <td className="cifra text-right">{porcentaje(fila.tasaAusentismo)}</td>
                    <td className="cifra text-right">{gs(fila.produccionGs)}</td>
                    <td className="cifra text-right font-medium">{gs(fila.cobradoGs)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Desplazable>
        </Panel>
      )}

      {comisiones.datos && comisiones.datos.length > 0 && (
        <Panel
          titulo="Comisiones"
          descripcion="Sobre lo efectivamente cobrado, no sobre lo presupuestado"
        >
          <Desplazable>
            <table className="tabla">
              <thead>
                <tr>
                  <th scope="col">Profesional</th>
                  <th scope="col" className="text-right">%</th>
                  <th scope="col" className="text-right">Base</th>
                  <th scope="col" className="text-right">Comisión</th>
                </tr>
              </thead>
              <tbody>
                {comisiones.datos.map((fila) => (
                  <tr key={fila.profesionalId}>
                    <td>{fila.profesional}</td>
                    <td className="cifra text-right">{fila.porcentaje}</td>
                    <td className="cifra text-right">{gs(fila.baseGs)}</td>
                    <td className="cifra text-right font-semibold">{gs(fila.comisionGs)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Desplazable>
        </Panel>
      )}
    </div>
  );
}
